import { Component } from '@theme/component';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A button that fills the nearest search input using the browser's speech
 * recognition API, so shoppers can search by voice instead of typing.
 *
 * Progressive enhancement only: browsers without SpeechRecognition (Firefox,
 * most non-Chromium mobile browsers) never see this button — it stays
 * `hidden` and the existing text search is completely unaffected.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} micButton - The microphone button.
 * @property {HTMLElement} announcer - Visually-hidden live region for screen readers.
 * @extends {Component<Refs>}
 */
export class VoiceSearchButton extends Component {
  requiredRefs = ['micButton', 'announcer'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {SpeechRecognition | null} */
  #recognition = null;

  /**
   * Whether recognizing a final result should submit the form immediately
   * (the dedicated search results page, which has no live predictive list)
   * rather than only filling the input and letting the shopper review
   * predictive results first (the header search modal).
   * @returns {boolean}
   */
  get #autoSubmit() {
    return this.hasAttribute('auto-submit');
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  get #targetInput() {
    const input = this.closest('form')?.querySelector('input[type="search"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  connectedCallback() {
    super.connectedCallback();

    const SpeechRecognitionImpl =
      window.SpeechRecognition ?? /** @type {any} */ (window).webkitSpeechRecognition;

    // No API support, or the page isn't HTTPS (SpeechRecognition requires a
    // secure context) — hide the button rather than show something broken.
    if (!SpeechRecognitionImpl || !window.isSecureContext) {
      this.hidden = true;
      return;
    }

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.micButton.addEventListener('click', this.#handleClick, { signal });

    const dialog = this.closest('dialog-component');
    dialog?.addEventListener(DialogCloseEvent.eventName, this.#stopListening, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#stopListening();
  }

  #handleClick = () => {
    if (this.dataset.state === 'listening') {
      this.#stopListening();
      return;
    }

    this.#startListening();
  };

  #startListening() {
    const targetInput = this.#targetInput;
    if (!targetInput) return;

    const SpeechRecognitionImpl =
      window.SpeechRecognition ?? /** @type {any} */ (window).webkitSpeechRecognition;

    const recognition = new SpeechRecognitionImpl();
    this.#recognition = recognition;

    recognition.lang = document.documentElement.lang || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      this.#setState('listening');
    };

    recognition.onresult = (/** @type {SpeechRecognitionEvent} */ event) => {
      const result = event.results[event.results.length - 1];
      const transcript = result?.[0]?.transcript ?? '';
      if (!transcript) return;

      this.#applyTranscript(targetInput, transcript, Boolean(result?.isFinal));
    };

    recognition.onerror = (/** @type {SpeechRecognitionErrorEvent} */ event) => {
      this.#handleError(event.error);
    };

    recognition.onend = () => {
      this.#setState('idle');
      this.#recognition = null;
    };

    try {
      recognition.start();
    } catch {
      // start() throws if called while already running (fast double-click) — ignore.
    }
  }

  #stopListening = () => {
    this.#recognition?.stop();
  };

  /**
   * @param {HTMLInputElement} input
   * @param {string} transcript
   * @param {boolean} isFinal
   */
  #applyTranscript(input, transcript, isFinal) {
    input.value = transcript;
    input.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: transcript }));

    if (isFinal && this.#autoSubmit) {
      input.form?.requestSubmit();
    }
  }

  /**
   * @param {string} error
   */
  #handleError(error) {
    if (error === 'no-speech' || error === 'aborted') {
      // Not a real error from the shopper's point of view — just stay idle.
      return;
    }

    this.#setState('error');

    const messageKey =
      error === 'not-allowed' || error === 'service-not-allowed'
        ? 'microphoneDenied'
        : error === 'network'
          ? 'networkError'
          : 'genericError';

    this.#announce(this.dataset[messageKey] ?? '');

    setTimeout(() => {
      if (this.dataset.state === 'error') this.#setState('idle');
    }, 3000);
  }

  /**
   * @param {'idle' | 'listening' | 'error'} state
   */
  #setState(state) {
    this.dataset.state = state;
    this.refs.micButton.setAttribute('aria-pressed', String(state === 'listening'));

    if (state === 'listening') {
      this.#announce(this.dataset.listeningLabel ?? '');
    }
  }

  /**
   * @param {string} message
   */
  #announce(message) {
    if (message) this.refs.announcer.textContent = message;
  }
}

if (!customElements.get('voice-search-button')) {
  customElements.define('voice-search-button', VoiceSearchButton);
}